import React, { useEffect, useMemo, useState } from "react";

import { Box, MenuItem } from "@mui/material";
import { Tooltip } from "@mui/material";
import { Column } from "Components/AppGrid/shipeezi-new-grid";
import _ from "lodash";
import { useTranslation } from "react-i18next";

import { CartonProductView } from "../../../../../models/carton";
import { Product } from "../../../../../models/product";
import { PurchaseOrder, PurchaseOrderProduct } from "../../../../../models/purchase.order";
import { DecimalFormatType } from "../../../../../util/formatDecimalValue";
import { getMeasurement, getWeightMeasurement } from "../../../../../util/getMeasurement";
import { Button, Input, AutoSuggest, Select } from "../../../../Form";
import { Color } from "../../../../Form/Button";
import InputMeasurement from "../../../../Form/InputMeasurement";
import ProductsScreen from "../../../../Orders/Maintenance/Products";
import Modal from "../../../Modal";
import { NotificationToast } from "../../../Toast/NotificationToast";
import { LIMIT_PRODUCTS_INSIDE_CARTONS } from "../../index";
import useStyles from "./styles";
import {
  CodeType,
  CodeTypeNumber,
  formatHSCode,
} from "../../../../../pages/private/Shared/Settings/Configurations/HSCommodityCode/utils";
import { CommodityCode } from "../../../../../models/commodity.code";
import CommodityCodeService from "../../../../../services/commodity.code.service";
import { UNIT_OF_MEASURE_OPTIONS } from "Util/constants";

type MandatoryFieldErrors = {
  [orderProdutId: string]: {
    cubicVolume: string;
    weight: string;
    quantityInCarton: string;
    hsCode: string;
    unitOfMeasure?: string;
    originCriterion: string;
  };
};

interface Props {
  products: PurchaseOrderProduct[];
  addProducts: (products: Product[]) => void;
  handleClose: () => void;
  addedProductsCount?: number;
  sscc: string;
  resourceCompanyId?: number;
  shouldShowUnitField?: boolean;
  showOriginCriterion?: boolean;
}

const ModalAddProduct = ({
  products,
  addProducts,
  handleClose,
  addedProductsCount,
  sscc,
  resourceCompanyId,
  shouldShowUnitField = false,
  showOriginCriterion = false,
}: Props) => {
  const { t: translate } = useTranslation();
  const classes = useStyles();

  const [orderProducts, setOrderProducts] = useState<PurchaseOrderProduct[]>(products);
  const [selectedProducts, setSelectedProducts] = useState<number[]>([]);
  const [errors, setErrors] = useState<MandatoryFieldErrors>({});
  const [triggerValidation, setTriggerValidation] = useState<boolean>(false);
  const [hasFormBeenSubmitted, setHasFormBeenSubmitted] = useState<boolean>(false);
  const [saveButtonTooltip, setSaveButtonTootip] = useState<string>("");

  const measurement = getMeasurement();
  const measurementWeight = getWeightMeasurement();

  const orderProductSelecteds = useMemo(() => {
    return orderProducts.filter((orderProduct) =>
      selectedProducts.some((selected) => Number(selected) === Number(orderProduct.id))
    );
  }, [orderProducts, selectedProducts]);

  const productsInCartonOrSelectedCount = (addedProductsCount ?? 0) + selectedProducts.length;

  const handleSelectProduct = (ids: number[]) => {
    setSelectedProducts(ids);
  };

  const hasSelectionMultipleOrders = (selection: PurchaseOrderProduct[]) => {
    return new Set(selection.map((item) => item.purchaseOrder?.reference)).size > 1;
  };

  const validateMandatoryFields = () => {
    let valid = true;

    setErrors({});

    let localErrors: MandatoryFieldErrors = {};

    orderProductSelecteds.forEach((orderProduct) => {
      const key = String(orderProduct.id);

      if (!orderProduct.cubicVolume || orderProduct.cubicVolume < 0) {
        valid = false;
        localErrors = {
          ...localErrors,
          [key]: {
            ...(localErrors[key] || {}),
            cubicVolume: translate("valueMustBeGreaterThanZero"),
          },
        };
      }
      if (!orderProduct.weight || orderProduct.weight < 0) {
        valid = false;
        localErrors = {
          ...localErrors,
          [key]: {
            ...(localErrors[key] || {}),
            weight: translate("valueMustBeGreaterThanZero"),
          },
        };
      }
      if (
        orderProduct.quantityInCarton &&
        !Number.isInteger(Number(orderProduct.quantityInCarton))
      ) {
        valid = false;
        localErrors = {
          ...localErrors,
          [key]: {
            ...(localErrors[key] || {}),
            quantityInCarton: translate("thisValueMustBeAnInteger"),
          },
        };
      }
      if (!orderProduct.quantityInCarton || orderProduct.quantityInCarton < 0) {
        valid = false;
        localErrors = {
          ...localErrors,
          [key]: {
            ...(localErrors[key] || {}),
            quantityInCarton: translate("valueMustBeGreaterThanZero"),
          },
        };
      }
      if (!orderProduct?.hsCode) {
        valid = false;
        localErrors = {
          ...localErrors,
          [key]: {
            ...(localErrors[key] || {}),
            hsCode: translate("insertAValidCode"),
          },
        };
      }

      if (shouldShowUnitField && !orderProduct?.unitOfMeasure) {
        valid = false;
        localErrors = {
          ...localErrors,
          [key]: {
            ...(localErrors[key] || {}),
            unitOfMeasure: translate("thisFieldIsRequired"),
          },
        };
      }

      if (
        showOriginCriterion &&
        (!orderProduct?.originCriterion || !["W", "P"].includes(orderProduct.originCriterion))
      ) {
        valid = false;
        localErrors = {
          ...localErrors,
          [key]: {
            ...(localErrors[key] || {}),
            originCriterion: translate("originCriterionMustBeWOrP"),
          },
        };
      }
    });

    setErrors(localErrors);

    return valid;
  };

  const handleAddProducts = () => {
    setHasFormBeenSubmitted(true);

    if (!validateMandatoryFields()) {
      NotificationToast.error(translate("youMustFillInTheFieldsInRed"), {
        id: "volume.and.weight.and.quantity.and.hscode.required",
      });

      return;
    }

    const purchaseOrdersProductsSelecteds = orderProductSelecteds.map(
      (orderProduct) =>
        ({
          ...orderProduct,
          product: {
            ...orderProduct.product,
            description: orderProduct?.description,
            colorDescription: orderProduct?.colorDescription,
            purchaseOrderProductId: orderProduct.id,
            quantityInCarton: orderProduct.quantityInCarton,
            weight: Number(orderProduct.weight || 0),
            cubicVolume: Number(orderProduct.cubicVolume || 0),
            hsCode: orderProduct.hsCode,
            commodityCodeId: orderProduct.commodityCodeId,
            originCriterion: orderProduct.originCriterion,
            unitOfMeasure: orderProduct.unitOfMeasure,
          },
        } as PurchaseOrderProduct)
    );

    const addedProducts: Product[] = [];

    for (const orderProductSelected of purchaseOrdersProductsSelecteds) {
      if (orderProductSelected?.product)
        addedProducts.push({
          ...orderProductSelected.product,
          poNumber: orderProductSelected?.purchaseOrder?.reference,
          cubicVolume: orderProductSelected.product?.cubicVolume,
          hsCode: orderProductSelected?.product?.hsCode,
          cfsCbm: orderProductSelected.cfsCbm,
          cfsWeight: orderProductSelected.cfsWeight,
          unitOfMeasure: orderProductSelected.unitOfMeasure,
          originCriterion: orderProductSelected.originCriterion,
        } as CartonProductView);
    }

    addProducts(addedProducts);
    handleClose();
  };

  const renderQuantityInCarton = (value: number) => {
    const [open, setOpen] = useState<boolean>(false);
    const handleClose = () => {
      setOpen(false);
    };

    const handleOpen = () => {
      if (inputError?.quantityInCarton) setOpen(true);
    };

    const orderProductIndex = orderProducts.findIndex((orderProduct) => orderProduct.id === value);
    const quantity =
      orderProductIndex === -1 ? 0 : orderProducts[orderProductIndex]?.quantityInCarton || 0;
    const inputError = errors[orderProducts[orderProductIndex].id!];

    const handleChangeQuantityFullfilled = (
      event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
      const orderProductsAux = _.cloneDeep(orderProducts);

      if (orderProductsAux[orderProductIndex]) {
        orderProductsAux[orderProductIndex].quantityInCarton = Number(event.target.value);

        setOrderProducts(orderProductsAux);
      }
      if (hasFormBeenSubmitted) setTriggerValidation((previousValue) => !previousValue);
    };

    return (
      <Tooltip
        classes={{ tooltip: classes.tooltipError }}
        title={inputError?.quantityInCarton ?? ""}
        placement="top"
        open={open && !!inputError?.quantityInCarton}
        onClose={handleClose}
        onOpen={handleOpen}
        PopperProps={{
          modifiers: [
            {
              name: "offset",
              options: {
                offset: [0, -13],
              },
            },
          ],
        }}
      >
        <div className={classes.quantityDiv}>
          <Input
            data-cy="quantity-input"
            type="number"
            min="0"
            error={!!inputError?.quantityInCarton}
            value={String(quantity)}
            onChange={handleChangeQuantityFullfilled}
          />
        </div>
      </Tooltip>
    );
  };

  const renderWeightField = (value: number) => {
    const [open, setOpen] = useState<boolean>(false);
    const handleClose = () => {
      setOpen(false);
    };

    const handleOpen = () => {
      if (inputError?.quantityInCarton) setOpen(true);
    };

    const orderProductIndex = orderProducts.findIndex((product) => product.id === value);
    const weight = orderProductIndex === -1 ? 0 : orderProducts[orderProductIndex]?.weight || 0;
    const inputError = errors[orderProducts[orderProductIndex].id!];

    const handleChangeWeight = (value: number) => {
      const orderProductsAux = _.cloneDeep(orderProducts);

      if (orderProductsAux[orderProductIndex]) {
        orderProductsAux[orderProductIndex].weight = Number(value);
        orderProductsAux[orderProductIndex].cfsWeight = Number(value);

        setOrderProducts(orderProductsAux);
      }
    };

    return (
      <Tooltip
        classes={{ tooltip: classes.tooltipError }}
        title={inputError?.weight ?? ""}
        placement="top"
        open={open && !!inputError?.weight}
        onClose={handleClose}
        onOpen={handleOpen}
        PopperProps={{
          modifiers: [
            {
              name: "offset",
              options: {
                offset: [0, -13],
              },
            },
          ],
        }}
      >
        <div className={classes.quantityDiv}>
          <InputMeasurement
            data-cy="quantity-input"
            min="0"
            value={weight}
            change={handleChangeWeight}
            error={!!inputError?.weight}
            measurement={measurementWeight}
            showMeasurement={false}
            decimalPlacesCustom={DecimalFormatType.MAERSK}
          />
        </div>
      </Tooltip>
    );
  };

  const renderCBMField = (value: number) => {
    const [open, setOpen] = useState<boolean>(false);
    const handleClose = () => {
      setOpen(false);
    };
    const handleOpen = () => {
      if (inputError?.cubicVolume) setOpen(true);
    };

    const orderProductIndex = orderProducts.findIndex((product) => product.id === value);
    const cbm = orderProductIndex === -1 ? 0 : orderProducts[orderProductIndex]?.cubicVolume || 0;
    const inputError = errors[orderProducts[orderProductIndex].id!];

    const handleChangeCBM = (value: number) => {
      const orderProductsAux = _.cloneDeep(orderProducts);

      if (orderProductsAux[orderProductIndex]) {
        orderProductsAux[orderProductIndex].cubicVolume = Number(value);
        orderProductsAux[orderProductIndex].cfsCbm = Number(value);

        setOrderProducts(orderProductsAux);
      }
    };

    return (
      <Tooltip
        classes={{ tooltip: classes.tooltipError }}
        title={inputError?.cubicVolume ?? ""}
        placement="top"
        open={open && !!inputError?.cubicVolume}
        onClose={handleClose}
        onOpen={handleOpen}
        PopperProps={{
          modifiers: [
            {
              name: "offset",
              options: {
                offset: [0, -13],
              },
            },
          ],
        }}
      >
        <div className={classes.quantityDiv}>
          <InputMeasurement
            data-cy="quantity-input"
            min="0"
            value={cbm}
            change={handleChangeCBM}
            error={!!inputError?.cubicVolume}
            measurement={measurement}
            showMeasurement={false}
            decimalPlacesCustom={DecimalFormatType.MAERSK}
          />
        </div>
      </Tooltip>
    );
  };

  const renderOriginCriterionField = (value: number) => {
    const [open, setOpen] = useState<boolean>(false);
    const handleClose = () => setOpen(false);
    const handleOpen = () => {
      if (inputError?.originCriterion) setOpen(true);
    };

    const orderProductIndex = orderProducts.findIndex((product) => product.id === value);
    const originCriterion =
      orderProductIndex === -1 ? "" : orderProducts[orderProductIndex]?.originCriterion || "";
    const inputError = errors[orderProducts[orderProductIndex].id!];

    const handleChangeOriginCriterion = (event: React.ChangeEvent<{ value: unknown }>) => {
      const orderProductsAux = _.cloneDeep(orderProducts);

      if (orderProductsAux[orderProductIndex]) {
        orderProductsAux[orderProductIndex].originCriterion = event.target.value as string;
        setOrderProducts(orderProductsAux);
      }
      if (hasFormBeenSubmitted) setTriggerValidation((prev) => !prev);
    };

    return (
      <Tooltip
        classes={{ tooltip: classes.tooltipError }}
        title={inputError?.originCriterion ?? ""}
        placement="top"
        open={open && !!inputError?.originCriterion}
        onClose={handleClose}
        onOpen={handleOpen}
        PopperProps={{
          modifiers: [{ name: "offset", options: { offset: [0, -13] } }],
        }}
      >
        <div className={classes.quantityDiv}>
          <Select
            value={originCriterion}
            onChange={handleChangeOriginCriterion as any}
            placeholder={translate("originCriterion")}
            displayEmpty
            error={!!inputError?.originCriterion}
          >
            <MenuItem value="W">W</MenuItem>
            <MenuItem value="P">P</MenuItem>
          </Select>
        </div>
      </Tooltip>
    );
  };

  const renderHSCodeField = (value: number) => {
    const [open, setOpen] = useState<boolean>(false);
    const handleClose = () => {
      setOpen(false);
    };
    const handleOpen = () => {
      if (inputError?.hsCode) setOpen(true);
    };

    const orderProductIndex = orderProducts.findIndex((product) => product.id === value);
    const hscode = orderProductIndex === -1 ? "" : orderProducts[orderProductIndex]?.hsCode || "";
    const inputError = errors[orderProducts[orderProductIndex].id!];

    const handleChangeHSCode = (code: CommodityCode) => {
      const orderProductsAux = _.cloneDeep(orderProducts);

      if (orderProductsAux[orderProductIndex]) {
        orderProductsAux[orderProductIndex].hsCode = String(code.hscode);
        orderProductsAux[orderProductIndex].commodityCodeId = code.id;
        orderProductsAux[orderProductIndex].commodityCode = code;

        setOrderProducts(orderProductsAux);
      }
    };

    return (
      <Tooltip
        classes={{ tooltip: classes.tooltipError }}
        title={inputError?.hsCode ?? ""}
        placement="top"
        open={open && !!inputError?.hsCode}
        onClose={handleClose}
        onOpen={handleOpen}
        PopperProps={{
          modifiers: [
            {
              name: "offset",
              options: {
                offset: [0, -13],
              },
            },
          ],
        }}
      >
        <div className={classes.quantityDiv}>
          <AutoSuggest
            data-cy="hscode-input"
            placeholder={translate("typeInToSearch")}
            getOptionLabel={(option: CommodityCode) => option?.hscode}
            getOptionSelected={(option: CommodityCode, value: CommodityCode) =>
              option.id === value.id
            }
            renderOption={(props, option) => (
              <div {...props}>
                `${formatHSCode(option?.hscode)} - ${option?.description}`
              </div>
            )}
            inputValue={hscode && formatHSCode(hscode)}
            onChange={(value: CommodityCode) => {
              if (!value?.hscode) return;
              handleChangeHSCode(value);
            }}
            removeArrow
            onSearch={(value) =>
              CommodityCodeService.getAll({
                companyId: resourceCompanyId,
                level: [
                  CodeTypeNumber[CodeType.HS_CODE],
                  CodeTypeNumber[CodeType.COMMODITY_CODE],
                ] as unknown as number,
                sortBy: JSON.stringify([["section", "ASC"]]),
                searchByOr: value
                  ? JSON.stringify([{ description: value }, { hscode: value }])
                  : "",
                include: JSON.stringify([
                  { association: "sectionEntity" },
                  { association: "chapterEntity" },
                ]),
              })
            }
            error={!!inputError?.hsCode}
          />
        </div>
      </Tooltip>
    );
  };

  const renderUnitOfMeasureField = (value: number) => {
    if (!shouldShowUnitField) return null;

    const [open, setOpen] = useState<boolean>(false);
    const handleClose = () => {
      setOpen(false);
    };
    const handleOpen = () => {
      if (inputError?.hsCode) setOpen(true);
    };

    const orderProductIndex = orderProducts.findIndex((product) => product.id === value);
    const unitOfMeasure =
      orderProductIndex === -1 ? "" : orderProducts[orderProductIndex]?.unitOfMeasure || "";
    const inputError = errors[orderProducts[orderProductIndex].id!];

    const handleChangeUnitOfMeasure = (value: string) => {
      const orderProductsAux = _.cloneDeep(orderProducts);

      if (orderProductsAux[orderProductIndex]) {
        orderProductsAux[orderProductIndex].unitOfMeasure = value;

        setOrderProducts(orderProductsAux);
      }
    };

    return (
      <Tooltip
        classes={{ tooltip: classes.tooltipError }}
        title={inputError?.hsCode ?? ""}
        placement="top"
        open={open && !!inputError?.hsCode}
        onClose={handleClose}
        onOpen={handleOpen}
        PopperProps={{
          modifiers: [
            {
              name: "offset",
              options: {
                offset: [0, -13],
              },
            },
          ],
        }}
      >
        <div className={classes.quantityDiv}>
          <Select
            required={shouldShowUnitField}
            value={unitOfMeasure}
            onChange={(e) => handleChangeUnitOfMeasure(e.target.value as string)}
            error={!!inputError?.unitOfMeasure}
          >
            {UNIT_OF_MEASURE_OPTIONS(translate).map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </div>
      </Tooltip>
    );
  };

  const columns: Column[] = [
    {
      name: "product",
      title: `${translate("sku")} #`,
      formatText: (product: Product) => product?.sku,
      className: classes.sku,
      searchIdentifier: "sku",
    },
    {
      name: "purchaseOrder",
      title: `${translate("order")} #`,
      formatText: (order: PurchaseOrder) => order?.reference,
      className: classes.poNumber,
      searchIdentifier: "reference",
    },
    {
      name: "description",
      title: translate("productName"),
      className: classes.description,
      searchIdentifier: "description",
    },
    ...(showOriginCriterion
      ? [
          {
            name: "id",
            title: translate("originCriterion"),
            formatComponent: renderOriginCriterionField,
            hideSearch: true,
            className: classes.originCriterion,
          },
        ]
      : []),
    {
      name: "id",
      title: translate("hsCommodityCode"),
      formatComponent: renderHSCodeField,
      searchIdentifier: "hscode",
      className: classes.hscode,
      hideSearch: true,
    },
    {
      name: "id",
      title: `${translate("volume")} (${(measurement?.code || "")?.toUpperCase()})`,
      formatComponent: renderCBMField,
      searchIdentifier: "cbm",
      className: classes.volume,
      hideSearch: true,
    },
    {
      name: "id",
      title: `${translate("weight")} (${(measurementWeight?.code || "")?.toUpperCase()})`,
      formatComponent: renderWeightField,
      searchIdentifier: "weight",
      className: classes.weight,
      hideSearch: true,
    },
    {
      name: "quantity",
      title: translate("quantityRequested"),
      hideSearch: true,
      className: classes.quantity,
    },
    {
      name: "quantityFullfilled",
      title: translate("quantityFullfilled"),
      hideSearch: true,
      className: classes.quantity,
    },
    {
      name: "id",
      title: translate("quantity"),
      formatComponent: renderQuantityInCarton,
      hideSearch: true,
      className: classes.quantity,
    },
    ...(shouldShowUnitField
      ? [
          {
            name: "id",
            title: translate("unitOfMeasure"),
            formatComponent: renderUnitOfMeasureField,
            hideSearch: true,
            className: classes.quantity,
          },
        ]
      : []),
  ];

  useEffect(() => {
    if (hasFormBeenSubmitted) {
      validateMandatoryFields();
    }
  }, [triggerValidation, orderProductSelecteds, hasFormBeenSubmitted]);

  useEffect(() => {
    if (selectedProducts.length > 0) {
      const uniqueTypeAndColorProductCount = new Set(
        orderProductSelecteds.map((orderProduct) => orderProduct.product.styleColor)
      ).size;

      if (uniqueTypeAndColorProductCount > 1) {
        setSaveButtonTootip(translate("theCartonCanOnlyContainProductsOfTheSameTypeAndColor"));
      } else if (productsInCartonOrSelectedCount > LIMIT_PRODUCTS_INSIDE_CARTONS) {
        setSaveButtonTootip(
          `${translate("theLimitOfProductsInsideACartonIs")} ${LIMIT_PRODUCTS_INSIDE_CARTONS}`
        );
      } else {
        if (hasSelectionMultipleOrders(orderProductSelecteds)) {
          setSaveButtonTootip(translate("theProductsMustBelongToTheSameOrder"));

          return;
        }
        setSaveButtonTootip("");
      }
    } else {
      setSaveButtonTootip(translate("youShouldSelectTheProducts"));
    }
  }, [selectedProducts]);

  const customRightButtons = (
    <>
      <Button
        variant="text"
        data-cy="button-cancel"
        onClick={handleClose}
        customColor={Color.CANCEL}
      >
        {translate("cancel")}
      </Button>

      <Tooltip
        classes={{ tooltip: classes.tooltipDisabled }}
        title={saveButtonTooltip}
        placement="top"
      >
        <div>
          <Button
            data-cy="button-save"
            onClick={handleAddProducts}
            customColor={Color.SUCCESS}
            disabled={selectedProducts.length < 1 || !!saveButtonTooltip.length}
            className={classes.rightButton}
          >
            {`${translate("add")}`}
          </Button>
        </div>
      </Tooltip>
    </>
  );

  return (
    <Modal
      className={classes.modalBody}
      title={translate("addProducts")}
      open={true}
      handleClose={handleClose}
      actionButtonProps={{
        textSave: `${translate("addProducts")} (${selectedProducts.length})`,
        onSave: handleAddProducts,
        onCancel: handleClose,
        saveDisabled: selectedProducts.length < 1,
      }}
      customRightButtons={customRightButtons}
    >
      <span className={classes.subtitle}>{`${translate(
        "selectProductsBelowToAddThemToSSCC"
      )} #${sscc?.replace(/[^0-9]/g, "")}`}</span>
      <Box className={classes.dialogContent}>
        <ProductsScreen
          modalProps={{
            modalView: true,
            selecteds: selectedProducts,
            handleSelect: handleSelectProduct,
            keyExtractor: (orderProduct: PurchaseOrderProduct) => String(orderProduct.id),
          }}
          supplierView
          products={orderProducts}
          overrideColumns={columns}
          updateOrder={() => {}}
          viewOnly={false}
        />
      </Box>
    </Modal>
  );
};

export default ModalAddProduct;
